/**
 * LoggedOutMosaic: 未ログイン時に日程調整部分の代わりに表示する固定モザイク（I261-77）。
 *
 * <p>日程調整画面（候補3マス＋「別の日時から選ぶ」＝計4マス）をベースに、日程の文字だけを
 * `filter: blur(2.5px)` で読めなくした固定のダミー表示と、直上に案内文を置いた「ログイン」（黒）／
 * 「新規会員登録」（赤）の2ボタン（横並び）から成る。実データ（空き枠・アンケート・形式ラベル等）は
 * 一切扱わない。形式切替・形式ラベル・目的欄・同意文・「都合の良い日時がない場合はこちら」は生成しない。
 *
 * <p>単一CV（SurveyPopup / PreBookingForm）と複数CV（MultiCvAppointmentList）で共通利用する。
 * `.spica-slot-list` / `.spica-slot-btn` / `.spica-slot-more-btn` / `.spica-confirm-button` の基本スタイルは
 * 呼び出し元の BOOKING_CARD_STYLES / MULTI_CV_STYLES を再利用し、本ファイルの
 * {@link LOGGED_OUT_MOSAIC_STYLES} は未ログイン固有の差分のみを持つ（呼び出し元CSSの末尾に連結すること）。
 *
 * <p>設計: ipros-team/HAL_9000 PR #312 の `I261/aidlc-docs/I261-77/construction/spica-tag-widget/functional-design/`（BR-I261-1〜15）、
 * 要件は同 `I261/aidlc-docs/I261-77/inception/requirements/requirements.md`。既存設計書への反映は spica の
 * `aidlc-docs/construction/u6-tag-widget/functional-design/`（BR-U6-1）。
 * Shadow DOM 内で動作するため `innerHTML` は使わず、文字列CSS定数でスタイルを適用する（NFR-2）。
 */
import { createElement } from "../core/shadowDomUtil";
import { BRAND_PRIMARY, LOGIN_BUTTON_BG, TEXT_PRIMARY } from "./designTokens";

/** 新規会員登録ボタンの hover 色（サービスサイトのヘッダー新規会員登録ボタンに合わせた明るい赤）。 */
const REGISTER_BUTTON_HOVER_BG = "#e34f54";

/** 固定のダミー日程ラベル（候補3マス。実際の日付とは連動しない）。 */
export const LOGGED_OUT_MOSAIC_SLOT_LABELS = ["3/10(月) 10:00〜", "3/10(月) 14:00〜", "3/11(火) 16:00〜"] as const;
/** 4マス目のラベル。 */
export const LOGGED_OUT_MOSAIC_MORE_LABEL = "別の日時から選ぶ";
/** ボタン直上の案内文。 */
export const LOGGED_OUT_MOSAIC_PROMPT = "掲載内容について確認・相談するには、ログインが必要です";
/** 「ログイン」ボタン（黒）の文言。 */
export const LOGGED_OUT_MOSAIC_LOGIN_LABEL = "ログイン";
/** 「新規会員登録」ボタン（赤）の文言。 */
export const LOGGED_OUT_MOSAIC_REGISTER_LABEL = "新規会員登録";

export const LOGGED_OUT_MOSAIC_STYLES = `
.spica-slot-text--masked { filter: blur(2.5px); user-select: none; }
/* 「日程」ラベル: モック（company-detail-schedule.html）準拠。単一CVのログイン済み表示には専用スタイルが無いため、未ログインモザイク内に限定して適用する。 */
.spica-logged-out-mosaic .spica-slot-label { font-size: 11px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; }
/* ダミー日程は押せない（pointer-events:none により hover も発火しないため :hover の上書きは不要）。 */
.spica-logged-out-mosaic .spica-slot-btn { pointer-events: none; cursor: default; }
.spica-login-prompt { margin: 8px 0 0; font-size: 12px; font-weight: 700; line-height: 1.6; color: ${TEXT_PRIMARY}; }
/* ログイン／新規会員登録の2ボタンは横並び・同じ幅。 */
.spica-logged-out-mosaic .spica-login-actions { display: flex; gap: 8px; margin-top: 8px; }
.spica-logged-out-mosaic .spica-login-actions .spica-confirm-button {
  flex: 1 1 0; min-width: 0; width: auto; margin-top: 0; box-sizing: border-box;
  /* 枠線を透明にして両ボタンの内寸をそろえる。flex で文字を縦横中央に置く（狭幅で折り返しても位置がずれない）。
     レコメンドCV既存の padding: 9px はこの共通ルールが後勝ちで上書きする（詳細度が同じで末尾に連結されるため）。 */
  display: flex; align-items: center; justify-content: center;
  border: 1px solid transparent; padding: 11px;
  text-align: center; text-decoration: none; line-height: 1.4; cursor: pointer;
}
/* ログイン: 黒背景（従来のログインボタンの色合い）。 */
.spica-logged-out-mosaic .spica-confirm-button.is-login { background: ${LOGIN_BUTTON_BG}; color: #fff; }
.spica-logged-out-mosaic .spica-confirm-button.is-login:hover { background: ${TEXT_PRIMARY}; }
/* 新規会員登録: 赤背景・白文字・枠線なし（サービスサイトのヘッダー新規会員登録ボタンに合わせたブランドカラー。枠線は共通ルールの transparent のまま）。 */
.spica-logged-out-mosaic .spica-confirm-button.is-register {
  background: ${BRAND_PRIMARY}; color: #fff;
}
.spica-logged-out-mosaic .spica-confirm-button.is-register:hover { background: ${REGISTER_BUTTON_HOVER_BG}; }
/* メインCVが無く先頭のレコメンドCVに2ボタンが出る場合、幅の狭いカードで見切れないよう折り返す（既存の nowrap を上書き）。 */
.spica-cv-item.recommend-cv .spica-logged-out-mosaic .spica-confirm-button { white-space: normal; }
`;

export interface LoggedOutMosaicOptions {
  /**
   * 「ログイン」ボタン（黒）の遷移先。呼び出し元が `buildWidgetLoginUrl(reason, analyticsCtx)` で組み立てて渡す
   * （LOGIN_PROMPT の発火はその呼出し時点＝描画時。現行どおり）。
   */
  loginUrl: string;
  /** 「新規会員登録」ボタン（赤）の遷移先。呼び出し元が `buildWidgetRegisterUrl(reason)` で組み立てて渡す（記録は発火しない）。 */
  registerUrl: string;
  /**
   * 案内文とログイン／新規会員登録のボタンを出すか（既定: true）。false のときはこれらを生成しない。
   * 複数CVのレコメンドCV（幅の狭いカード）では、ポップアップ内のログイン導線をメインCVのボタンに集約するため false にする。
   * LOGIN_PROMPT の発火は呼び出し元が `buildWidgetLoginUrl` を呼ぶ時点なので、この指定では変わらない。
   */
  showLoginAction?: boolean;
}

/** ダミー日程の1マス（操作不可。文字のみぼかす）。 */
function buildDummySlot(label: string, isMore: boolean): HTMLLIElement {
  const li = createElement("li");
  const button = createElement("button", {
    className: isMore ? "spica-slot-btn spica-slot-more-btn" : "spica-slot-btn",
    attrs: { type: "button", tabindex: "-1" },
  });
  button.appendChild(createElement("span", { className: "spica-slot-text--masked", text: label }));
  li.appendChild(button);
  return li;
}

/**
 * 未ログイン固定モザイクを構築する。
 * 構成: 「日程」ラベル → ダミー4マス → 案内文 → 「ログイン」（黒）と「新規会員登録」（赤）の横並び2リンク
 * （案内文とリンクは `showLoginAction: false` で省略できる）。
 */
export function renderLoggedOutMosaic(options: LoggedOutMosaicOptions): HTMLElement {
  const root = createElement("div", {
    className: "spica-logged-out-mosaic",
    attrs: { "data-testid": "logged-out-mosaic" },
  });

  // 「日程」ラベルも、日程候補（aria-hidden）と同様に読み上げ対象外にする（中身の無い見出しだけが読まれるのを避ける）。
  root.appendChild(createElement("p", { className: "spica-slot-label", text: "日程", attrs: { "aria-hidden": "true" } }));

  // ダミー日程は読み上げ対象外（誤った日付を読み上げない）。ボタンはフォーカスさせない。
  const list = createElement("ul", {
    className: "spica-slot-list",
    attrs: { "aria-hidden": "true", "data-testid": "logged-out-mosaic-slot-list" },
  });
  for (const label of LOGGED_OUT_MOSAIC_SLOT_LABELS) {
    list.appendChild(buildDummySlot(label, false));
  }
  list.appendChild(buildDummySlot(LOGGED_OUT_MOSAIC_MORE_LABEL, true));
  root.appendChild(list);

  // 案内文とログインボタンを出さない指定のときは、生成せずに返す（CSS で隠さない。BR-I261-4 と同じ方針）。
  if (options.showLoginAction === false) return root;

  root.appendChild(createElement("p", { className: "spica-login-prompt", text: LOGGED_OUT_MOSAIC_PROMPT }));

  // ログインと新規会員登録は別々のボタンにして横並びにする（1つのボタンに2つの要素を持たせない）。
  // どちらも同じタブで遷移する <a href>。ログインは従来のログイン導線、新規会員登録は会員サイトの登録画面へ。
  const actions = createElement("div", { className: "spica-login-actions", attrs: { "data-testid": "logged-out-mosaic-actions" } });
  actions.appendChild(
    createElement("a", {
      className: "spica-confirm-button is-login",
      text: LOGGED_OUT_MOSAIC_LOGIN_LABEL,
      attrs: { href: options.loginUrl, "data-testid": "logged-out-mosaic-login-button" },
    })
  );
  actions.appendChild(
    createElement("a", {
      className: "spica-confirm-button is-register",
      text: LOGGED_OUT_MOSAIC_REGISTER_LABEL,
      attrs: { href: options.registerUrl, "data-testid": "logged-out-mosaic-register-button" },
    })
  );
  root.appendChild(actions);

  return root;
}
